/**
 * Tipos de OpenRTA en R2 (`ALBRTA-a`, 1-oct-2026).
 *
 * Plazas de alojamiento del Registro de Turismo de Andalucía publicado
 * (OpenRTA). Las produce `scripts/download-openrta.mjs`; las lee
 * `OpenRTAR2Service` (prefijo R2 `openrta/`, un fichero por INE).
 *
 * Son «plazas del registro publicado», no un censo: el registro trae, por
 * ejemplo, solo 12 campings para toda Andalucía. Se muestran siempre
 * separadas del aforo declarado en el plan y de la capacidad DGPC.
 *
 * Fuente: Consejería de Turismo, Justicia, Desregulación y Administración
 * Local. Licencia CC BY 4.0 (atribución obligatoria en app y exportación).
 *
 * @module types/openrta
 */

/** Tipo corto de alojamiento reglado (sin viviendas turísticas). */
export type TipoAlojamientoRTA =
  | 'hotel'
  | 'hostal'
  | 'pension'
  | 'albergue'
  | 'hotel-apartamento'
  | 'apartamento'
  | 'casa-rural'
  | 'complejo-rural'
  | 'camping'
  | 'area-autocaravanas'
  | 'otro';

/** Por qué un alojamiento no se propone sin revisión (DOP-6 a′). */
export type AvisoAlojamientoRTA = 'sin_coordenadas' | 'fuera_del_municipio';

/** Un alojamiento reglado del registro. Coordenadas en EPSG:25830. */
export interface AlojamientoRTA {
  id: number;
  /** Nombre en caja RAE con las tildes de la tabla revisada (`ALBRTA-b2a`). */
  nombre: string | null;
  /** Nombre tal cual lo publica el registro (ausente en los ficheros anteriores a `ALBRTA-b2a`). */
  nombreRegistro?: string | null;
  tipo: TipoAlojamientoRTA;
  tipoRegistro: string | null;
  grupo: string | null;
  categoria: string | null;
  /** `tot_gen_places`; null si el registro no da plazas. */
  plazas: number | null;
  /** `tot_gen_ua` (habitaciones o unidades). */
  unidades: number | null;
  /** `maximum_capacity_camp`, solo campings. */
  capacidadCamping: number | null;
  x: number | null;
  y: number | null;
  refcat: string | null;
  direccion: string | null;
  codigoRegistro: string | null;
  web: string | null;
  aviso: AvisoAlojamientoRTA | null;
  /** INE donde cae el punto cuando no es el del registro. */
  ineDelPunto: string | null;
}

/** Número de viviendas turísticas (solo recuento: sus plazas no se publican por municipio). */
export interface ViviendasTuristicasRTA {
  /** Viviendas de uso turístico. */
  vut: number;
  /** Viviendas turísticas de alojamiento rural. */
  vtar: number;
}

/** Fichero de un municipio en R2. */
export interface OpenRTAMunicipio {
  fuente: string;
  licencia: string;
  /** Última actualización del registro según la API (ISO). */
  fechaRegistro: string | null;
  /** Cuándo se descargó (ISO). */
  fechaDescarga: string;
  ine: string;
  /** Grafía del municipio en el registro. */
  municipioRegistro: string;
  alojamientos: AlojamientoRTA[];
  viviendas: ViviendasTuristicasRTA | null;
}

/** Texto de atribución (CC BY 4.0) para la app y la exportación. */
export const ATRIBUCION_OPENRTA =
  'Plazas del Registro de Turismo de Andalucía publicado (OpenRTA), Junta de Andalucía · CC BY 4.0';
